import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import MagnifyingGlassIcon from '@mui/icons-material/Search';
import PixelResolutionIcon from '@mui/icons-material/CenterFocusStrong';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import Tooltip from '@mui/material/Tooltip';
import WarningIcon from '@mui/icons-material/Warning';
import {Attribution, MousePosition, ScaleLine} from 'ol/control';
import {connect, useSelector} from 'react-redux';
import {useAuth} from '@apps-common/auth-react';
import {useTranslation} from 'react-i18next';

import CopyButton from '../ui/copy-button.jsx';
import formatLocation from '@explorer/util/format-location';
import i18n from '@explorer/i18n';
import {
  AttributionContainer,
  CloseButton,
  CurrentLocation,
  Label,
  ScaleContainer,
  Wrapper,
} from './components.jsx';
import {
  MESSAGES_KEYS,
  getValue as getMessageSeen,
} from '@explorer/util/local-messages';
import {
  TOURS,
  TOUR_STAGES,
  coordinateSystems,
  preferenceGroups,
} from '@explorer/constants';
import {clearMapPin} from '@explorer/actions/map';
import {endTour, startTour} from '@explorer/actions/tour';
import {getMetersPerPixel} from '@explorer/util/map';
import {getTileConfigs} from '@explorer/selectors/tile-groups.js';
import {setPreference} from '@explorer/actions/settings';
import {trackEvent} from '@explorer/analytics';
import {withDarkTheme} from '@explorer/styles';

const COORDINATE_SYSTEM_ORDER = [
  coordinateSystems.dd,
  coordinateSystems.dms,
  coordinateSystems.mgrs,
];

export const isImageryOverZoomed = (tileConfig, zoom) => {
  return (
    tileConfig?.rasterConfigs.length > 0 &&
    tileConfig?.rasterConfigs.some(r => zoom > r.maxZoom)
  );
};

const StatusBar = ({addMapControl, startTour, endTour, ...props}) => {
  const auth = useAuth();
  const {t} = useTranslation();
  const [langChange, setLangChange] = useState(false);
  const [res, setRes] = useState();
  const mousePositionRef = useRef();
  const mousePositionCtl = useRef();
  const coordinate = useRef(null);

  const scaleLineCtl = useRef();
  const scaleLineRef = useRef();

  const attributionCtl = useRef();
  const attributionRef = useRef();

  const showOverzoomMessage = useSelector(state => {
    const tileConfigs = getTileConfigs(state, auth);
    const tileMode = state.tiles.mode;
    const zoom = state.map.zoom;

    return isImageryOverZoomed(tileConfigs[tileMode], zoom);
  });

  const isDMS = props.coordinateSystem === coordinateSystems.dms;
  const isMGRS = props.coordinateSystem === coordinateSystems.mgrs;
  const nextSystem = current =>
    COORDINATE_SYSTEM_ORDER[(COORDINATE_SYSTEM_ORDER.indexOf(current) + 1) % 3];

  const measurementUnits =
    props.measurementSystem === 'usc' ? 'us' : props.measurementSystem;

  const pinnedLocation = props.pinnedLocation
    ? formatLocation(props.pinnedLocation, props.coordinateSystem, res)
    : '';

  const setCoordinatePreference = () => {
    const getSystem = nextSystem(props.coordinateSystem);
    props.setPreference({
      key: preferenceGroups.coordinateSystem,
      value: getSystem,
    });
    trackEvent('Coordinate System changed', {
      system: getSystem,
    });
  };

  const coordinateFormatter = useCallback(
    coord => {
      coordinate.current = coord;
      if (langChange) {
        setLangChange(false);
      }

      if (isMGRS) {
        const resolution = mousePositionCtl.current
          .getMap()
          .getView()
          .getResolution();
        setRes(resolution);
      }

      return formatLocation(coord, props.coordinateSystem, res);
    },
    [isMGRS, res, props.coordinateSystem, langChange]
  );

  i18n.on('languageChanged', function () {
    setLangChange(true);
  });

  const onAddMapControls = () => {
    mousePositionCtl.current = new MousePosition({
      className: 'pl-mouse-position',
      coordinateFormat: coordinateFormatter,
      target: mousePositionRef.current,
      undefinedHTML: '',
    });
    addMapControl(mousePositionCtl.current);

    scaleLineCtl.current = new ScaleLine({
      className: 'pl-scale-line',
      target: scaleLineRef.current,
      units: measurementUnits || 'metric',
    });

    addMapControl(scaleLineCtl.current);

    attributionCtl.current = new Attribution({
      target: attributionRef.current,
      collapsible: false,
    });

    addMapControl(attributionCtl.current);

    return () => {
      mousePositionCtl.current.getMap().removeControl(mousePositionCtl.current);
    };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(onAddMapControls, []);

  useEffect(() => {
    mousePositionCtl.current.setCoordinateFormat(coordinateFormatter);
    if (coordinate.current) {
      const map = mousePositionCtl.current.getMap();
      const pixel = map.getPixelFromCoordinate(coordinate.current);
      mousePositionCtl.current.updateHTML_(pixel);
    }
  }, [coordinateFormatter, isDMS, langChange]);

  useEffect(() => {
    scaleLineCtl.current.setUnits(measurementUnits);
  }, [measurementUnits]);

  useEffect(() => {
    const messageSeen = getMessageSeen(MESSAGES_KEYS.OVERZOOM);
    if (!messageSeen && !props.mainTourActive) {
      if (showOverzoomMessage && !props.overzoomTourActive) {
        startTour(TOURS.OVERZOOM);
      } else if (!showOverzoomMessage && props.overzoomTourActive) {
        endTour(TOURS.OVERZOOM);
      }
    }
  }, [
    endTour,
    props.mainTourActive,
    props.overzoomTourActive,
    showOverzoomMessage,
    startTour,
  ]);

  return (
    <Wrapper>
      <AttributionContainer ref={attributionRef} noWrap />

      {pinnedLocation && (
        <Box display="flex">
          <Label noWrap>
            <GpsFixedIcon fontSize="inherit" />
            {pinnedLocation}
          </Label>
          <CopyButton
            buttonStyleOverrides={{
              fontSize: theme => theme.typography.body2.fontSize,
            }}
            onCopy={() => trackEvent('Copied coordinates to clipboard')}
            showNotification={false}
            size="small"
            textToCopy={pinnedLocation}
            title={t('statusBar.copyCoordinates', 'coordinates')}
          />
          <CloseButton
            onClick={() => props.clearMapPin()}
            title={t('statusBar.removePin', 'Remove')}
          />
        </Box>
      )}

      <CurrentLocation
        ref={mousePositionRef}
        onClick={setCoordinatePreference}
      />

      <Label data-qe="zoom-level">
        {!showOverzoomMessage ? (
          <MagnifyingGlassIcon fontSize="inherit" />
        ) : (
          <Fade in={showOverzoomMessage}>
            <Box
              color="warning.main"
              fontSize="body2.fontSize"
              display="flex"
              mr={0.5}
              data-tour={TOUR_STAGES.OVERZOOM}
            >
              <Tooltip
                title={
                  getMessageSeen(MESSAGES_KEYS.OVERZOOM)
                    ? t('statusBar.tooltipOverzoom')
                    : ''
                }
              >
                <WarningIcon fontSize="inherit" color="inherit" />
              </Tooltip>
            </Box>
          </Fade>
        )}
        <Tooltip title={t('statusBar.tooltipZoom')}>
          <span>{parseFloat(props.zoom.toFixed(2))}</span>
        </Tooltip>
      </Label>

      <Tooltip title={t('statusBar.tooltipVisibleResolution')}>
        <Label data-qe="zoom-meters-pixel" sx={{minWidth: 110}}>
          <PixelResolutionIcon fontSize="inherit" />
          {t('statusBar.visibleResolution', {
            resolution: getMetersPerPixel(props.center[1], props.zoom).toFixed(
              Math.ceil(props.zoom / 8)
            ),
          })}
        </Label>
      </Tooltip>
      <ScaleContainer ref={scaleLineRef} />
    </Wrapper>
  );
};

StatusBar.propTypes = {
  addMapControl: PropTypes.func,
  coordinateSystem: PropTypes.string.isRequired,
  measurementSystem: PropTypes.string.isRequired,
  clearMapPin: PropTypes.func,
  pinnedLocation: PropTypes.array,
  setPreference: PropTypes.func,
  zoom: PropTypes.number.isRequired,
  center: PropTypes.array.isRequired,
  startTour: PropTypes.func,
  endTour: PropTypes.func,
  mainTourActive: PropTypes.bool,
  overzoomTourActive: PropTypes.bool,
};

const mapStateToProps = state => ({
  coordinateSystem: state.settings.coordinateSystem,
  measurementSystem: state.settings.measurementSystem,
  pinnedLocation: state.map.mapPin,
  zoom: state.map.zoom,
  center: state.map.center,
  mainTourActive: state.tour.run && state.tour.tour === TOURS.MAIN,
  overzoomTourActive: state.tour.run && state.tour.tour === TOURS.OVERZOOM,
});

const mapDispatch = {
  clearMapPin,
  setPreference,
  startTour,
  endTour,
};

export default connect(mapStateToProps, mapDispatch)(withDarkTheme(StatusBar));
